import { useTranslation } from "react-i18next";
import { ChevronDown } from "lucide-react";
import { AE, AU, BH, CA, EG, EU, GB, IN, JO, KW, OM, QA, SA, TR, US } from "country-flag-icons/react/3x2";
import { CURRENCIES, useCurrency } from "@/lib/CurrencyContext";
import { DropdownMenu, DropdownMenuContent, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

// SVG flags (MIT, country-flag-icons): emoji flags don't render on Windows Chrome.
const FLAGS = { AED: AE, USD: US, EUR: EU, GBP: GB, SAR: SA, KWD: KW, QAR: QA, BHD: BH, OMR: OM,
  EGP: EG, JOD: JO, CAD: CA, AUD: AU, TRY: TR, INR: IN };

export function CurrencyFlag({ code, className = "" }) {
  const Flag = FLAGS[code];
  return Flag ? <Flag aria-hidden="true" className={`h-3 w-[18px] shrink-0 rounded-[2px] ring-1 ring-white/15 ${className}`} /> : null;
}

export default function CurrencySelector({ variant = "header", className = "" }) {
  const { t } = useTranslation();
  const { currency, setCurrency } = useCurrency();
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger data-testid={`currency-selector-${variant}`} dir="ltr"
        aria-label={t("currency.label", { defaultValue: "Display currency" })}
        title={t("currency.note", { defaultValue: "Prices shown for reference. You are charged in AED." })}
        className={`inline-flex items-center gap-1.5 bg-black/30 border border-white/10 hover:border-white/30 rounded-full ps-2 pe-1.5 py-1 font-mono text-[10px] tracking-[0.15em] text-white/80 focus:outline-none focus:ring-1 focus:ring-red-brand transition-colors ${className}`}>
        <CurrencyFlag code={currency} />
        <span data-testid={`currency-selected-${variant}`}>{currency}</span>
        <ChevronDown size={12} className="text-white/50" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" dir="ltr" data-testid={`currency-menu-${variant}`}
        className="w-32 max-h-[60vh] overflow-y-auto bg-black border-white/10 p-1">
        <DropdownMenuRadioGroup value={currency} onValueChange={setCurrency}>
          {CURRENCIES.map((c) => (
            <DropdownMenuRadioItem key={c} value={c} data-testid={`currency-option-${c}`}
              className="font-mono text-[11px] tracking-[0.12em] text-white/80 focus:bg-white/10 focus:text-white data-[state=checked]:text-red-brand cursor-pointer gap-2">
              <CurrencyFlag code={c} />
              <span>{c}</span>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// "Charged in AED" disclosure for cart / checkout / price blocks.
export function ChargedInAed({ aedTotal, testid = "charged-in-aed", className = "", prominent = false }) {
  const { t } = useTranslation();
  const { active, converted, aedText, fxSource, fxAttribution, fxUnavailable } = useCurrency();
  const amount = aedTotal != null ? aedText(aedTotal) : "";
  return (
    <div data-testid={testid} className={`text-[11px] text-white/55 ${className}`}>
      {converted && amount ? (
        <div data-testid={`${testid}-amount`} className={prominent ? "font-mono text-sm text-white" : "text-white/85"}>
          <bdi>{t("currency.youPay", { amount, defaultValue: "Amount charged: {{amount}}" })}</bdi>
        </div>
      ) : (
        <div dir="ltr">{t("currency.chargedIn", { defaultValue: "Charged in AED" })}{amount && <> · <span className="text-white/80">{amount}</span></>}</div>
      )}
      {converted && (
        <p data-testid={`${testid}-notice`} className="mt-0.5 text-white/45 leading-snug">
          {t("currency.notice", { currency: active })}
          {fxSource && <> ({fxAttribution ? <a href={fxAttribution} target="_blank" rel="noreferrer" className="underline">{t("currency.ratesBy")} {fxSource}</a> : `${t("currency.ratesBy")} ${fxSource}`})</>}
        </p>
      )}
      {fxUnavailable && <div className="text-white/35">{t("currency.unavailable")}</div>}
    </div>
  );
}

// Slim, non-blocking notice under the header whenever a non-AED display currency is active.
export function CurrencyNotice() {
  const { t } = useTranslation();
  const { active, converted } = useCurrency();
  if (!converted) return null;
  return (
    <div data-testid="currency-notice" className="border-t border-white/10 bg-black/90 px-6 md:px-10 py-1 text-center text-[10px] md:text-[11px] text-white/60">
      {t("currency.notice", { currency: active })}
    </div>
  );
}
